/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { formatArPhoneDisplay } from '@shared/phone';
import { formatLocalDate } from '@shared/vueltoDateTime';
import type { SupplierRecord } from '../../domain/catalogSupplierTypes';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { typography } from '../../../shared/theme/typography';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';

type SupplierMasterTableProps = {
  suppliers: SupplierRecord[];
  onOpenDetail: (supplier: SupplierRecord) => void;
};

function formatLocation(supplier: SupplierRecord): string {
  const city = supplier.city?.trim();
  const state = supplier.state?.trim();
  if (city && state) return `${city}, ${state}`;
  return city ?? state ?? '—';
}

function formatContact(supplier: SupplierRecord): string {
  const name = supplier.contactName?.trim();
  const phoneRaw = supplier.contactPhone?.trim();
  const phone = phoneRaw ? formatArPhoneDisplay(phoneRaw) || phoneRaw : null;
  const email = supplier.contactEmail?.trim();
  if (name && phone) return `${name} · ${phone}`;
  if (name && email) return `${name} · ${email}`;
  return name ?? phone ?? email ?? '—';
}
export function SupplierMasterTable({ suppliers, onOpenDetail }: SupplierMasterTableProps) {
  if (suppliers.length === 0) return null;

  return (
    <ScrollView
      horizontal
      style={styles.tableOuter}
      contentContainerStyle={styles.tableOuterContent}
      showsHorizontalScrollIndicator={false}
    >
      <View style={styles.table}>
        <View style={[styles.row, styles.headerRow]}>
          <Text style={[styles.headerCell, styles.nameCell]}>Proveedor</Text>
          <Text style={[styles.headerCell, styles.codeCell]}>Código</Text>
          <Text style={[styles.headerCell, styles.contactCell]}>Contacto</Text>
          <Text style={[styles.headerCell, styles.locationCell]}>Ubicación</Text>
          <Text style={[styles.headerCell, styles.taxCell]}>CUIT</Text>
          <Text style={[styles.headerCell, styles.dateCell]}>Creado</Text>
          <Text style={[styles.headerCell, styles.statusCell]}>Estado</Text>
          <View style={styles.chevronCell} />
        </View>

        {suppliers.map((supplier) => {
          const codeText = supplier.code?.trim() ? supplier.code.trim() : '—';
          const taxText = supplier.taxId?.trim() ? supplier.taxId.trim() : '—';
          const dateText = supplier.createdAt ? formatLocalDate(supplier.createdAt) : '—';
          const contactText = formatContact(supplier);
          const locationText = formatLocation(supplier);

          return (
            <TouchableOpacity
              key={supplier.id}
              style={[styles.row, !supplier.isActive && styles.rowInactive]}
              onPress={() => onOpenDetail(supplier)}
              accessibilityRole="button"
              accessibilityLabel={`Detalle de ${supplier.name}, ${supplier.isActive ? 'activo' : 'inactivo'}`}
            >
              <View style={styles.nameCell}>
                <Text style={styles.itemName} numberOfLines={1}>
                  {supplier.name}
                </Text>
                {supplier.notes?.trim() ? (
                  <Text style={styles.itemMeta} numberOfLines={1}>
                    {supplier.notes.trim()}
                  </Text>
                ) : null}
              </View>
              <Text style={[styles.codeCell, styles.codeText]} numberOfLines={1}>
                {codeText}
              </Text>
              <Text style={[styles.contactCell, styles.contactText]} numberOfLines={2}>
                {contactText}
              </Text>
              <Text style={[styles.locationCell, styles.locationText]} numberOfLines={1}>
                {locationText}
              </Text>
              <Text style={[styles.taxCell, styles.taxText]} numberOfLines={1}>
                {taxText}
              </Text>
              <Text style={[styles.dateCell, styles.dateText]} numberOfLines={1}>
                {dateText}
              </Text>
              <View style={styles.statusCell}>
                <StatusPill
                  label={supplier.isActive ? 'Activo' : 'Inactivo'}
                  tone={supplier.isActive ? 'success' : 'danger'}
                  compact
                />
              </View>
              <View style={styles.chevronCell}>
                <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  tableOuter: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    marginBottom: spacing.sm,
  },
  tableOuterContent: {
    minWidth: '100%',
  },
  table: {
    ...ticketSurfaces.paperSheet,
    width: '100%',
    minWidth: 760,
  },
  row: {
    minHeight: 60,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 13,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  rowInactive: {
    opacity: 0.55,
  },
  headerRow: {
    minHeight: 40,
    backgroundColor: ticketColors.paper2,
  },
  headerCell: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  nameCell: {
    flexGrow: 3,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 180,
    gap: 2,
  },
  codeCell: {
    flexGrow: 0,
    flexShrink: 1,
    width: 96,
    minWidth: 72,
  },
  contactCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 140,
    maxWidth: 220,
  },
  locationCell: {
    flexGrow: 0,
    flexShrink: 1,
    width: 128,
    minWidth: 96,
  },
  taxCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 120,
  },
  dateCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 108,
  },
  statusCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 96,
  },
  chevronCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 20,
    alignItems: 'center',
  },
  itemName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  itemMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  codeText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  contactText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    lineHeight: 17,
  },
  locationText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  taxText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  dateText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
